import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from 'react-router-dom';

// Import components
import Header from "./components/Header";
import CustomerInfoForm from "./components/CustomerInfoForm";
import CabinetTable from "./components/CabinetTable";
import ActionButtons from "./components/ActionButtons";
import OrderSummary from "./components/OrderSummary";

// Import services
import { apiService } from "../../../services/api";

// Import data and utilities
import { colorCodeData, cabinetTypes, shakerData, fixedPriceItems, pvcRollingShutterSizes, profileHandlePrices } from "./data/constants";
import { useOrderCalculator } from "./hooks/useOrderCalculator";
import usePdfGenerator from "./hooks/usePdfGenerator";

const CreateCabinetOrder = () => {
  const [invoiceId, setInvoiceId] = useState("");
  const [country, setCountry] = useState("Custom");
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    zip: "",
    city: "",
    state: "",
    date: "",
    discount: 0,
  });
  const [cabinets, setCabinets] = useState([getEmptyCabinet()]);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const { 
    grandTotal, 
    subtotal,
    salesTax,
    finalTotal,
    calculateTotal, 
    calculateSqft 
  } = useOrderCalculator({
    cabinets,
    customerInfo,
    country,
  });

  const { handleDownloadPDF } = usePdfGenerator({
    invoiceId,
    customerInfo,
    cabinets,
    country,
    grandTotal,
    subtotal,
    salesTax,
    finalTotal,
  });

  useEffect(() => {
    testBackendConnection();
  }, []);

  useEffect(() => {
    const id = "INV-" + Date.now();
    setInvoiceId(id);
  }, []);

  useEffect(() => {
    const currentColor = cabinets.length > 0 ? cabinets[0].color : "";
    const currentMaterial = cabinets.length > 0 ? cabinets[0].material : "";
    const currentUnitCost = cabinets.length > 0 ? cabinets[0].unitCost : "";
    
    setCabinets([getEmptyCabinet(currentColor, currentMaterial, currentUnitCost)]);
  }, [country]);

  const testBackendConnection = async () => {
    try {
      console.log("🔄 Testing backend connection...");
      const result = await apiService.testConnection();
      console.log("✅ Backend connection successful:", result);
      toast.success("✅ Connected to backend server!");
    } catch (error) {
      console.error("❌ Backend connection failed:", error);
      
      try {
        console.log("🔄 Trying direct fetch test...");
        const response = await fetch('http://localhost:5000/api/test', {
          method: 'GET',
          mode: 'cors'
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log("✅ Direct fetch successful:", data);
          toast.info("⚠️ API service issue but backend is accessible");
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (fetchError) {
        console.error("❌ Direct fetch also failed:", fetchError);
        // toast.error("❌ Cannot connect to backend. Please ensure server is running on port 5000.");
      }
    }
  };

  const isCabinetValid = (cab) => {
    if (country === "Shaker") {
      return cab.type && cab.description && cab.unitCost !== "" && cab.qty > 0;
    }
    
    // Check if it's PVC Rolling Shutter
    if (cab.type === "PVC Rolling Shutter") {
      return cab.type && cab.pvcSize && cab.color && cab.qty > 0;
    }
    
    // Check if it's Profile Handle
    if (cab.type === "Profile Handle") {
      return cab.type && cab.profileHandleInches && cab.qty > 0;
    }
    
    // For other fixed price items
    const isFixedPriceItem = fixedPriceItems[cab.type];
    
    if (isFixedPriceItem) {
      // Fixed price items only need type and quantity
      return cab.type && cab.qty > 0;
    } else {
      // Regular cabinets need all dimensions
      return (
        cab.type &&
        cab.height &&
        cab.width &&
        cab.color &&
        cab.material &&
        cab.unitCost !== ""
      );
    }
  };

  const handleCabinetChange = (index, field, value) => {
    setCabinets(prevCabinets => {
      const updated = [...prevCabinets];
      updated[index] = {
        ...updated[index],
        [field]: value
      };

      if (field === "color") {
        const color = colorCodeData[value];
        if (color) {
          updated[index] = {
            ...updated[index],
            material: color.material,
            // Only set unitCost for regular cabinets, not for PVC Rolling Shutter or Profile Handle
            ...(updated[index].type !== "PVC Rolling Shutter" && updated[index].type !== "Profile Handle" && {
              unitCost: color.shutterPrice
            })
          };
        } else {
          updated[index] = {
            ...updated[index],
            material: "",
            ...(updated[index].type !== "PVC Rolling Shutter" && updated[index].type !== "Profile Handle" && {
              unitCost: ""
            })
          };
        }

        // REMOVED: Auto-populate color to all other rows
        // Each row now maintains its own independent color selection
      }

      if (field === "type") {
        // Handle Profile Handle
        if (country === "Custom" && value === "Profile Handle") {
          updated[index] = {
            ...updated[index],
            description: "Profile Handle - Select Inches",
            height: "N/A",
            width: "N/A",
            material: "Profile Handle",
            drawerSlides: "",
            hinges: "",
            color: "",
            unitCost: "" // Reset unitCost - it will be set when inches are selected
          };
        }
        
        // Handle PVC Rolling Shutter
        else if (country === "Custom" && value === "PVC Rolling Shutter") {
          updated[index] = {
            ...updated[index],
            description: "PVC Rolling Shutter - Select Size",
            height: "N/A",
            width: "N/A",
            material: "PVC Rolling Shutter",
            drawerSlides: "",
            hinges: "",
            color: "",
            unitCost: "" // Reset unitCost - it will be set when size is selected
          };
        }
        
        // Handle other fixed price items for custom cabinets
        else if (country === "Custom" && fixedPriceItems[value] && value !== "PVC Rolling Shutter" && value !== "Profile Handle") {
          updated[index] = {
            ...updated[index],
            unitCost: fixedPriceItems[value],
            height: "N/A",
            width: "N/A",
            description: `${value} - Fixed Price Item`,
            drawerSlides: "",
            hinges: "",
            color: "",
            material: ""
          };
        }
        
        // Handle Shaker items
        else if (country === "Shaker") {
          const shakerItem = shakerData.find((item) => item.item === value);
          if (shakerItem) {
            updated[index] = {
              ...updated[index],
              description: shakerItem.description,
              unitCost: shakerItem.unitcost,
              height: "",
              width: "",
              color: "",
              material: ""
            };
          }
        }
        
        // Handle regular custom cabinets
        else if (country === "Custom") {
          updated[index] = {
            ...updated[index],
            description: "",
            height: "",
            width: "",
            color: "",
            material: "",
            unitCost: "",
            drawerSlides: "",
            hinges: "",
            pvcSize: "",
            profileHandleInches: ""
          };
        }
      }

      // Handle PVC Rolling Shutter size selection
      if (field === "pvcSize" && country === "Custom" && updated[index].type === "PVC Rolling Shutter") {
        const sizePrice = pvcRollingShutterSizes[value] || 0;
        updated[index] = {
          ...updated[index],
          unitCost: sizePrice,
          description: `PVC Rolling Shutter - ${value}`
        };
      }

      // Handle Profile Handle inch selection
      if (field === "profileHandleInches" && country === "Custom" && updated[index].type === "Profile Handle") {
        const inchPrice = profileHandlePrices[value] || 0;
        updated[index] = {
          ...updated[index],
          unitCost: inchPrice,
          description: `Profile Handle - ${value} inch`
        };
      }

      return updated;
    });
  };

  const addCabinet = () => {
    const lastCabinet = cabinets[cabinets.length - 1];
    if (!isCabinetValid(lastCabinet)) {
      toast.error("Please complete the current row before adding a new one.");
      return;
    }
    
    // When adding a new row, start with empty color (no auto-population)
    setCabinets([...cabinets, getEmptyCabinet()]);
  };

  const handleDeleteCabinet = (index) => {
    if (cabinets.length === 1) {
      toast.error("At least one cabinet row is required.");
      return;
    }
    setCabinets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const currentCabinet = cabinets[index];
      
      if (isCabinetValid(currentCabinet)) {
        if (index === cabinets.length - 1) {
          addCabinet();
          setTimeout(() => {
            const nextRowInput = document.querySelector(`[data-row-index="${index + 1}"] input, [data-row-index="${index + 1}"] select`);
            if (nextRowInput) nextRowInput.focus();
          }, 100);
        } else {
          const nextInput = event.target.closest('td').nextElementSibling?.querySelector('input, select');
          if (nextInput) nextInput.focus();
        }
      } else {
        toast.error("Please complete all required fields in the current row.");
      }
    }
  };

  const saveOrder = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);

      if (!customerInfo.firstName || !customerInfo.phone || !customerInfo.address) {
        toast.error("Please fill in all customer fields.");
        return;
      }

      const incomplete = cabinets.some((cab) => !isCabinetValid(cab));
      if (incomplete) {
        toast.error("Please complete all cabinet rows before saving.");
        return;
      }

      const totalCostValue = finalTotal;

      const orderData = {
        invoiceId,
        customerInfo: {
          ...customerInfo,
          date: customerInfo.date || new Date().toISOString().split('T')[0]
        },
        cabinets,
        country,
        totalCost: totalCostValue,
        subtotal: subtotal,
        salesTax: salesTax,
        finalTotal: finalTotal,
        overallDiscount: parseFloat(customerInfo.discount) || 0,
        status: 'Pending'
      };

      console.log("📦 Sending order data:", orderData);

      const result = await apiService.createOrder(orderData);

      if (result.success) {
        toast.success("🎉 Order Saved Successfully!");
        
        setCustomerInfo({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          address: "",
          zip: "",
          city: "",
          state: "",
          date: "",
          discount: 0,
        });
        setCabinets([getEmptyCabinet()]);
        
        const newInvoiceId = "INV-" + Date.now();
        setInvoiceId(newInvoiceId);
      } else {
        toast.error(result.message || "Failed to save order");
      }
    } catch (error) {
      console.error('💥 Save order error:', error);
      toast.error(`Error saving order: ${error.message}. Please check if backend server is running.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4 w-full">
      <div className="cabinet-order-container w-full mx-auto p-6 md:p-8 fade-in">
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        
        <div className="w-full">
          <Header 
            invoiceId={invoiceId}
            country={country}
            setCountry={setCountry}
          />
        </div>
        
        <div className="w-full mt-6">
          <CustomerInfoForm 
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
          />
        </div>
        
        <div className="w-full mt-6">
          <CabinetTable
            cabinets={cabinets}
            country={country}
            handleCabinetChange={handleCabinetChange}
            handleDeleteCabinet={handleDeleteCabinet}
            handleKeyDown={handleKeyDown}
            calculateTotal={calculateTotal}
            calculateSqft={calculateSqft}
            isCabinetValid={isCabinetValid}
          />
        </div>
        
        <div className="flex justify-center mt-6 w-full">
          <button
            onClick={addCabinet}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center space-x-2"
          >
            <span>+</span>
            <span>Add New Cabinet Row</span>
          </button>
        </div>

        <div className="w-full mt-6">
          <OrderSummary 
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            grandTotal={grandTotal}
            subtotal={subtotal}
            salesTax={salesTax}
            finalTotal={finalTotal}
          />
        </div>
        
        <div className="w-full mt-6">
          <ActionButtons 
            onSave={saveOrder}
            onDownloadPdf={handleDownloadPDF}
            isSaving={isSaving}
          />
        </div>
      </div>
    </div>
  );
};

function getEmptyCabinet(color = "", material = "", unitCost = "") {
  return {
    type: "",
    height: "",
    width: "",
    color: color,
    material: material,
    unitCost: unitCost,
    drawerSlides: "",
    hinges: "",
    discount: 0,
    description: "",
    qty: 1,
    pvcSize: "", // For PVC Rolling Shutter
    profileHandleInches: "" // For Profile Handle
  };
}

export default CreateCabinetOrder;